import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useConfig } from "../contexts/config-context";
import { ScrollArea, Button, Stack, Title, Text, Modal, TextInput, Group, ActionIcon, CopyButton, Tooltip, Box, Code } from "@mantine/core";
import { IconCode, IconCopy, IconCheck } from "@tabler/icons-react";

export function ConfigSidebar() {
  const {
    guiContainerRef,
    callSaveHandler,
    isGeneratorPage,
  } = useConfig();
  const [searchParams] = useSearchParams();
  const variantId = searchParams.get('variantId');

  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);

  if (!isGeneratorPage) {
    return null;
  }

  const embedUrl = variantId
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/${variantId}`
    : '';

  const embedCode = variantId
    ? `<iframe src="${embedUrl}" width="800" height="600" frameborder="0" allowfullscreen></iframe>`
    : '';

  const handleGetEmbedLink = () => {
    if (!variantId) {
      alert('Please save the variant first to get an embed link.');
      return;
    }
    setIsEmbedModalOpen(true);
  };

  return (
    <>
      <Stack h="100%" gap={0}>
        <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Title order={4}>Element Configuration</Title>
            <Text size="xs" c="dimmed">
              Adjust parameters to customize your element
            </Text>
        </Box>
        
        <ScrollArea style={{ flex: 1 }}>
            <Box p="md" ref={guiContainerRef} className="gui-container" />
        </ScrollArea>

        <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <Stack gap="xs">
                <Button onClick={callSaveHandler} fullWidth color="cyan">
                    Save Variant
                </Button>
                <Button onClick={handleGetEmbedLink} variant="default" fullWidth leftSection={<IconCode size={16} />}>
                    Get Embed Link
                </Button>
            </Stack>
        </Box>
      </Stack>

      <Modal opened={isEmbedModalOpen} onClose={() => setIsEmbedModalOpen(false)} title="Embed Your Variant" size="lg" centered>
          <Stack>
            <Text size="sm" c="dimmed">
              Copy the code below to embed this variant on your website.
            </Text>
            
            <TextInput
                label="Embed URL"
                value={embedUrl}
                readOnly
                rightSection={
                    <CopyButton value={embedUrl} timeout={2000}>
                    {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                        <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                        </Tooltip>
                    )}
                    </CopyButton>
                }
            />

            <Box>
                <Text size="sm" fw={500} mb={5}>Embed Code</Text>
                <Box pos="relative">
                    <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {embedCode}
                    </Code>
                    <CopyButton value={embedCode} timeout={2000}>
                    {({ copied, copy }) => (
                        <ActionIcon 
                            color={copied ? 'teal' : 'gray'} 
                            variant="subtle" 
                            onClick={copy} 
                            style={{ position: 'absolute', top: 5, right: 5 }}
                        >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                    )}
                    </CopyButton>
                </Box>
            </Box>
            
            <Text size="xs" c="dimmed">
              Note: The variant must be set to public for the embed to work on external websites.
            </Text>
          </Stack>
      </Modal>
    </>
  );
}
